import React from 'react';
import { Linkedin } from 'lucide-react';
import { recommendation as rec, emergentRule } from '../data/mock';
import { Card, Label } from './primitives';

/* The letter is rendered verbatim. The only emphasis is a highlighter over
   the closing sentence; the words themselves are never changed. */
const Paragraph = ({ text }) => {
  if (rec.highlight && text.startsWith(rec.highlight)) {
    return (
      <p>
        <span className="mark font-semibold text-ink">{rec.highlight}</span>
        {text.slice(rec.highlight.length)}
      </p>
    );
  }
  return <p>{text}</p>;
};

const Author = ({ className = '' }) => (
  <div className={`flex items-center gap-3 md:flex-col md:items-start ${className}`}>
    <img
      src={rec.photo}
      width={800}
      height={800}
      loading="lazy"
      decoding="async"
      alt={rec.author}
      className="w-14 h-14 sm:w-16 sm:h-16 md:w-36 md:h-36 rounded-full md:rounded-xl object-cover border-[1.5px] border-ink shadow-hard-sm shrink-0"
    />
    <div className="min-w-0 md:mt-1">
      <p className="text-[15px] font-bold text-ink leading-snug">{rec.author}</p>
      <p className="eyebrow mt-0.5">
        {rec.title} · {rec.company}
      </p>
      <a
        href={rec.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${rec.author} on LinkedIn`}
        className="mt-1.5 inline-flex items-center gap-1.5 p-2 -m-2 text-[13px] font-semibold text-ink-soft hover:text-flag transition-colors"
      >
        <Linkedin size={16} aria-hidden="true" />
        LinkedIn
      </a>
    </div>
  </div>
);

const Recommendation = () => (
  <section id="letter" className="bg-cream py-16 sm:py-24 scroll-mt-20">
    <div className="max-w-5xl mx-auto px-5 sm:px-8">
      <Label className="mb-3">Letter of recommendation</Label>
      <h2 className="text-2xl sm:text-4xl font-bold text-ink tracking-[-0.02em] max-w-3xl leading-tight">
        “{rec.pullQuote}”
      </h2>
      <p className="mt-3 text-sm sm:text-base text-ink-soft">
        {rec.author}, {rec.title} at {rec.company}, on my eight months there.
      </p>

      <Card
        tint="paper"
        className="mt-10 p-5 sm:p-8 relative md:grid md:grid-cols-[9rem_1fr] md:gap-8"
      >
        <span
          aria-hidden="true"
          className="absolute -top-5 left-5 bg-paper border-[1.5px] border-ink rounded-lg px-2 pt-1 text-flag text-4xl font-bold leading-none"
        >
          “
        </span>
        <Author />
        <div className="mt-5 pt-5 border-t border-ink/15 md:mt-0 md:pt-0 md:border-t-0">
          <div className="space-y-4 text-[15px] sm:text-base text-ink-soft leading-relaxed">
            {rec.paragraphs.map((para) => (
              <Paragraph key={para.slice(0, 32)} text={para} />
            ))}
          </div>
          <p className="mt-5 font-mono text-xs text-subtle">
            — {rec.author}, {rec.title}, {rec.company}
          </p>
        </div>
      </Card>

      <p className="mt-10 text-base sm:text-lg font-semibold text-ink max-w-2xl leading-snug">
        <span className="text-flag">✦ </span>
        {emergentRule.lesson}
      </p>
    </div>
  </section>
);

export default Recommendation;
